// src/hooks/useCityBoundaries.js
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { detectCityFromURL, getCityInfo } from '../utils/cityDetector'
import { useCities } from '../context/CitiesContext'

/**
 * Hook que detecta la ciudad actual y proporciona sus límites y configuración
 * @returns {Object} Información de la ciudad y sus límites
 */
export const useCityBoundaries = () => {
  const location = useLocation()
  const { getCityBySlug } = useCities()
  const [currentCity, setCurrentCity] = useState(null)
  const [cityInfo, setCityInfo] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const citySlug = detectCityFromURL(location.pathname)
    setCurrentCity(citySlug)

    // IMPORTANTE: Limpiar cityInfo inmediatamente para evitar usar datos stale
    // Esto previene que el mapa vuele al centro de la ciudad anterior
    // mientras espera los datos de la nueva ciudad
    setCityInfo(null)

    if (citySlug) {
      setLoading(true)

      // Obtener config de la ciudad desde el contexto
      const cityConfig = getCityBySlug(citySlug)

      if (cityConfig) {
        getCityInfo(cityConfig)
          .then(info => {
            setCityInfo(info)
            setLoading(false)
          })
          .catch(error => {
            console.error(`Error cargando cityInfo para ${citySlug}:`, error)
            setCityInfo(null)
            setLoading(false)
          })
      } else {
        console.warn(`Ciudad ${citySlug} no encontrada en el contexto`)
        setCityInfo(null)
        setLoading(false)
      }
    } else {
      setCityInfo(null)
      setLoading(false)
    }
  }, [location.pathname, getCityBySlug])

  return {
    currentCity,
    cityInfo,
    cityName: cityInfo?.name || null,
    cityCenter: cityInfo?.center || null,
    cityZoom: cityInfo?.zoom || 12,
    cityBounds: cityInfo?.bounds || null,
    cityBoundary: cityInfo?.boundary || null,
    loading,
  }
}

/**
 * Hook que aplica los límites de la ciudad al mapa de Mapbox
 * Configura maxBounds para restringir el viewport
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 */
const enableMapInteractions = map => {
  map.dragPan.enable()
  map.scrollZoom.enable()
  map.touchZoomRotate.enable()
  map.keyboard.enable()
  map.doubleClickZoom.enable()
}

const disableMapInteractions = map => {
  map.dragPan.disable()
  map.scrollZoom.disable()
  map.touchZoomRotate.disable()
  map.keyboard.disable()
  map.doubleClickZoom.disable()
}

export const useApplyCityBounds = (map, mapLoaded, isAdmin = false) => {
  const { cityBounds, currentCity, loading } = useCityBoundaries()

  useEffect(() => {
    if (!map || !mapLoaded || loading) return

    try {
      if (isAdmin) {
        map.setMaxBounds(null)
        enableMapInteractions(map)
        return
      }

      if (currentCity && cityBounds) {
        // Add a padding of 30% of the canton's span so Mapbox doesn't
        // enforce a minimum zoom that traps the user at the canton edge
        const lngSpan = Math.abs(cityBounds[1][0] - cityBounds[0][0])
        const latSpan = Math.abs(cityBounds[1][1] - cityBounds[0][1])
        const lngPad = lngSpan * 0.3
        const latPad = latSpan * 0.3
        const paddedBounds = [
          [cityBounds[0][0] - lngPad, cityBounds[0][1] - latPad],
          [cityBounds[1][0] + lngPad, cityBounds[1][1] + latPad],
        ]
        map.setMaxBounds(paddedBounds)
        enableMapInteractions(map)
      } else if (!currentCity) {
        // Vista de país sin cantón: limpiar bounds anteriores y bloquear interacción
        map.setMaxBounds(null)
        disableMapInteractions(map)
      } else {
        // currentCity presente pero bounds aún no cargados
        map.setMaxBounds(null)
      }
    } catch (error) {
      console.error('Error al aplicar límites al mapa:', error)
    }

    return () => {
      if (map) enableMapInteractions(map)
    }
  }, [map, mapLoaded, cityBounds, currentCity, loading, isAdmin])

  return { currentCity, cityBounds }
}

/**
 * Hook que añade una capa visual de los límites de la ciudad al mapa
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 * @param {Object} options - Opciones de estilo y configuración
 */
export const useAddBoundaryLayer = (map, mapLoaded, options = {}) => {
  const { cityBoundary, currentCity, loading } = useCityBoundaries()

  const {
    lineColor = '#2196F3',
    lineWidth = 2,
    fillColor = '#2196F3',
    fillOpacity = 0.05,
    showFill = true,
    showLine = true,
    beforeLayer = 'clusters',
    initialVisible = true,
  } = options

  // Ref para leer el valor de initialVisible en el momento en que se añaden las capas,
  // sin convertirlo en dependencia del effect (evita re-añadir capas en cada toggle).
  const initialVisibleRef = useRef(initialVisible)
  initialVisibleRef.current = initialVisible

  useEffect(() => {
    if (!map || !mapLoaded || !cityBoundary || loading) return

    if (!cityBoundary.features || !cityBoundary.features.length) {
      console.error('cityBoundary no tiene features válidos')
      return
    }

    const sourceId = `boundary-${currentCity}`
    const lineLayerId = `boundary-line-${currentCity}`
    const fillLayerId = `boundary-fill-${currentCity}`

    let mounted = true

    const hasStyle = () => {
      try {
        return Boolean(map.getStyle?.())
      } catch {
        return false
      }
    }

    const waitForLayer = (layerId, timeout = 5000) => {
      return new Promise(resolve => {
        const startTime = Date.now()

        const checkLayer = () => {
          if (!mounted) {
            resolve(false)
            return
          }

          if (!hasStyle()) {
            if (Date.now() - startTime > timeout) {
              resolve(false)
              return
            }
            setTimeout(checkLayer, 100)
            return
          }

          if (map.getLayer(layerId)) {
            resolve(true)
            return
          }

          if (Date.now() - startTime > timeout) {
            resolve(false)
            return
          }

          setTimeout(checkLayer, 100)
        }

        checkLayer()
      })
    }

    const cleanup = () => {
      if (!hasStyle()) return

      try {
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch (error) {
        console.error('Error en cleanup de límites:', error)
      }
    }

    const addBoundaryLayers = async () => {
      cleanup()

      const layerExists = await waitForLayer(beforeLayer)

      if (!mounted) return

      try {
        const ensureBoundaryOrder = (retries = 40) => {
          if (!mounted || !hasStyle()) return

          const referenceLayer = map.getLayer(beforeLayer)
          if (!referenceLayer) {
            if (retries > 0) {
              setTimeout(() => ensureBoundaryOrder(retries - 1), 100)
            }
            return
          }

          try {
            if (showFill && map.getLayer(fillLayerId)) {
              map.moveLayer(fillLayerId, beforeLayer)
            }
            if (showLine && map.getLayer(lineLayerId)) {
              map.moveLayer(lineLayerId, beforeLayer)
            }
          } catch (error) {
            if (retries > 0) {
              setTimeout(() => ensureBoundaryOrder(retries - 1), 100)
            }
          }
        }

        if (!hasStyle()) return

        map.addSource(sourceId, {
          type: 'geojson',
          data: cityBoundary,
        })

        if (showFill) {
          map.addLayer(
            {
              id: fillLayerId,
              type: 'fill',
              slot: 'top',
              source: sourceId,
              layout: {
                visibility: initialVisibleRef.current ? 'visible' : 'none',
              },
              paint: {
                'fill-color': fillColor,
                'fill-opacity': fillOpacity,
              },
            },
            layerExists ? beforeLayer : undefined
          )
        }

        if (showLine) {
          map.addLayer(
            {
              id: lineLayerId,
              type: 'line',
              slot: 'top',
              source: sourceId,
              layout: {
                visibility: initialVisibleRef.current ? 'visible' : 'none',
              },
              paint: {
                'line-color': lineColor,
                'line-width': lineWidth,
                'line-dasharray': [3, 3],
              },
            },
            layerExists ? beforeLayer : undefined
          )
        }

        ensureBoundaryOrder()
      } catch (error) {
        console.error('Error al añadir capas de límites:', error)
      }
    }

    addBoundaryLayers()

    return () => {
      mounted = false
      cleanup()
    }
  }, [
    map,
    mapLoaded,
    cityBoundary,
    currentCity,
    loading,
    beforeLayer,
    lineColor,
    lineWidth,
    fillColor,
    fillOpacity,
    showFill,
    showLine,
    // initialVisible intencionalmente excluido: se lee desde initialVisibleRef.current
    // para evitar que un toggle del usuario re-añada las capas innecesariamente
  ])

  return { currentCity, loading }
}
